/**
 * Application initialization and core setup
 */

import { initI18n, t } from './i18n/config';
import { initFirebase, isFirebaseInitialized } from './services/firebase';
import { renderSignInView } from './views/SignIn';

/**
 * Initialize the Kassie App
 */
export async function initializeApp() {
  // Initialize internationalization
  await initI18n();

  // Initialize Firebase
  await initFirebase();

  // Check if Firebase is configured
  if (!isFirebaseInitialized()) {
    showFirebaseSetupRequired();
    return;
  }

  // Set up authentication state listener
  const { onAuthStateChange } = await import('./services/auth');
  onAuthStateChange((user) => {
    if (user) {
      // User is signed in
      console.log('User authenticated:', user.email);

      // Safety: remove loading screen after 5s max (prevents infinite hang)
      const safetyTimer = setTimeout(() => {
        const ls = document.querySelector('.loading-screen');
        if (ls) { ls.remove(); console.warn('⚠️ Loading screen removed by safety timer'); }
      }, 5000);

      showAuthenticatedView(user).then(() => {
        clearTimeout(safetyTimer);
        const loadingScreen = document.querySelector('.loading-screen');
        if (loadingScreen) loadingScreen.remove();
      }).catch((err) => {
        clearTimeout(safetyTimer);
        console.error('Failed to show authenticated view:', err);
        const loadingScreen = document.querySelector('.loading-screen');
        if (loadingScreen) loadingScreen.remove();
      });
    } else {
      // User is signed out
      console.log('User not authenticated');
      const loadingScreen = document.querySelector('.loading-screen');
      if (loadingScreen) loadingScreen.remove();
      renderSignInView();
    }
  });
}

/**
 * Show Firebase setup required message
 */
function showFirebaseSetupRequired() {
  // Remove loading screen
  const loadingScreen = document.querySelector('.loading-screen');
  if (loadingScreen) {
    loadingScreen.remove();
  }

  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="setup-required-container">
      <div class="setup-card">
        <img src="https://kassie.media/svg/branding/KassieNuweBlou-2.svg" alt="Kassie" width="200">
        <h1>${t('app.firebaseSetup.title')}</h1>
        <p>${t('app.firebaseSetup.description')}</p>

        <div class="setup-steps">
          <h3>${t('app.firebaseSetup.quickSetup')}</h3>
          <ol>
            <li>${t('app.firebaseSetup.step1')} <a href="https://console.firebase.google.com" target="_blank">console.firebase.google.com</a></li>
            <li>${t('app.firebaseSetup.step2')}</li>
            <li>${t('app.firebaseSetup.step3')}</li>
            <li>${t('app.firebaseSetup.step4')} <code>frontend/.env</code> ${t('app.firebaseSetup.step4b')}</li>
            <li>${t('app.firebaseSetup.step5')}</li>
          </ol>
        </div>

        <div class="setup-note">
          <p><strong>${t('app.firebaseSetup.note')}</strong> ${t('app.firebaseSetup.seeDocsFor')} <code>docs/setup-guide.md</code> ${t('app.firebaseSetup.seeDocsFor2')}</p>
        </div>

        <button onclick="location.reload()" class="btn-primary">${t('app.firebaseSetup.reloadPage')}</button>
      </div>
    </div>
  `;
}

/**
 * Show view for authenticated users
 */
async function showAuthenticatedView(user) {
  try {
    // Call user API to ensure user record exists in backend
    console.log('🔄 Ensuring user record exists in backend...');
    const { userApi } = await import('./services/userApi.js');
    const userProfile = await userApi.getCurrentUser();
    console.log('✅ User record confirmed:', userProfile);

    // Store user profile for use in other parts of the app
    window.currentUserProfile = userProfile;
  } catch (error) {
    console.error('❌ Failed to create/fetch user record:', error);

    // Check if registration was blocked (403)
    if (error.message && error.message.includes('403')) {
      showRegistrationBlockedView(user);
      return;
    }

    // Continue with the app even if user API fails - this is non-blocking
    // The user can still use the app, but some features might not work
  }

  // Check if this is an invitation URL
  const urlParams = new URLSearchParams(window.location.search);
  const invitationToken = urlParams.get('token');

  if (invitationToken && window.location.pathname === '/invite') {
    // Show invitation acceptance view
    const { renderInvitationAcceptance } = await import('./views/InvitationAcceptance');
    const app = document.getElementById('app');

    // Import invitation styles
    await import('./styles/invitation-acceptance.css');

    renderInvitationAcceptance(invitationToken, app);
  } else {
    // Load and render normal admin interface
    const { renderAdminView } = await import('./views/Admin');
    renderAdminView(user);

    // Initialize session timeout for admin users
    const { initSessionTimeout } = await import('./utils/sessionTimeout');
    initSessionTimeout();
  }
}

/**
 * Show registration blocked view with early access request form.
 * Displayed when the registration guard returns 403.
 */
async function showRegistrationBlockedView(user) {
  const { signOut } = await import('./services/auth');
  const { api } = await import('./services/api.js');

  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="registration-blocked-container" style="display:flex;align-items:center;justify-content:center;min-height:100vh;padding:2rem;">
      <div class="registration-blocked-card" style="background:#fff;border-radius:12px;padding:2rem;max-width:480px;width:100%;box-shadow:0 4px 20px rgba(0,0,0,0.1);">
        <div style="text-align:center;margin-bottom:1.5rem;">
          <img src="https://kassie.media/svg/branding/KassieAvatarNoBorder.svg" alt="Kassie" width="80" style="border:3px solid var(--admin-border,#333);border-radius:50%;">
          <h2 style="margin:1rem 0 0.5rem;">${t('subscription.settings.registrationClosed', 'Registrasie is tans gesluit')}</h2>
          <p style="color:#666;font-size:0.9rem;">${t('subscription.settings.requestAccess', 'Versoek vroeë toegang en ons sal jou laat weet sodra jy kan registreer.')}</p>
        </div>

        <form id="earlyAccessForm" style="display:flex;flex-direction:column;gap:1rem;">
          <div>
            <label style="display:block;font-weight:600;margin-bottom:0.25rem;font-size:0.875rem;">${t('subscription.settings.nameLabel', 'Naam')}</label>
            <input type="text" id="earlyAccessName" value="${user.displayName || ''}" class="admin-input" required />
          </div>
          <div>
            <label style="display:block;font-weight:600;margin-bottom:0.25rem;font-size:0.875rem;">${t('subscription.settings.emailLabel', 'E-posadres')}</label>
            <input type="email" id="earlyAccessEmail" value="${user.email || ''}" class="admin-input" required />
          </div>
          <div id="earlyAccessMessage" style="display:none;padding:0.75rem;border-radius:8px;font-size:0.875rem;text-align:center;"></div>
          <button type="submit" class="admin-btn admin-btn-primary" id="earlyAccessSubmitBtn" style="width:100%;padding:12px;">
            ${t('subscription.settings.requestEarlyAccess', 'Versoek Vroeë Toegang')}
          </button>
          <button type="button" id="earlyAccessSignOutBtn" class="admin-btn" style="width:100%;padding:12px;">
            ${t('auth.signOut', 'Teken Uit')}
          </button>
        </form>
      </div>
    </div>
  `;

  // Handle form submission
  const form = document.getElementById('earlyAccessForm');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const nameInput = document.getElementById('earlyAccessName');
    const emailInput = document.getElementById('earlyAccessEmail');
    const msgEl = document.getElementById('earlyAccessMessage');
    const submitBtn = document.getElementById('earlyAccessSubmitBtn');

    submitBtn.disabled = true;
    msgEl.style.display = 'none';

    try {
      await api.post('/api/early-access/request', {
        email: emailInput.value.trim(),
        displayName: nameInput.value.trim(),
      });

      // Success
      msgEl.textContent = t(
        'subscription.settings.earlyAccessConfirmation',
        'Dankie! Ons sal jou laat weet sodra jy toegang het.'
      );
      msgEl.style.display = 'block';
      msgEl.style.background = '#d4edda';
      msgEl.style.color = '#155724';

      // Hide form fields
      nameInput.parentElement.style.display = 'none';
      emailInput.parentElement.style.display = 'none';
      submitBtn.style.display = 'none';
    } catch (err) {
      submitBtn.disabled = false;
      if (err.message && err.message.includes('409')) {
        msgEl.textContent = t(
          'subscription.settings.alreadyRequested',
          'Jy het reeds vroeë toegang versoek.'
        );
      } else {
        msgEl.textContent = t(
          'subscription.settings.saveError',
          'Iets het fout gegaan. Probeer weer.'
        );
      }
      msgEl.style.display = 'block';
      msgEl.style.background = '#f8d7da';
      msgEl.style.color = '#721c24';
    }
  });

  // Handle sign out
  document.getElementById('earlyAccessSignOutBtn').addEventListener('click', async () => {
    await signOut();
  });
}
